import { yupResolver } from "@hookform/resolvers/yup";
import { cn } from "@omni/utils/styles";
import { FC, useEffect } from "react";
import { FormProvider } from "react-hook-form";

import { CheckBoxField } from "@/components/Form/RHF/FormFields/CheckboxField/CheckBoxField.component";
import { HiddenField } from "@/components/Form/RHF/FormFields/HiddenField/hiddenField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { ProductConfigurationVisualProps } from "@/utils/productConfiguration/productConfiguration.types";

import { LicensePlateInput } from "./licensePlaceInput.component";
import {
    LicensePlateFormValues,
    LicensePlateValidationSchema
} from "./licensePlate.schema";
import {
    LicensePlateConfiguration,
    LicensePlateTypes
} from "./licensePlate.types";

const FORM_ID = "license-plate-form";

const LicensePlate: FC<
    ProductConfigurationVisualProps<LicensePlateConfiguration>
> = ({
    setConfigurationValid,
    onConfigurationChange,
    configuration,
    className
}: ProductConfigurationVisualProps<LicensePlateConfiguration>) => {
    const { chosenLicensePlateText, chosenLicensePlateType } =
        configuration || {
            chosenLicensePlateType:
                LicensePlateTypes.LICENSE_PLATE_CAR_STANDARD,
            chosenLicensePlateText: ""
        };
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            isPersonalized:
                chosenLicensePlateType ===
                LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED,
            licensePlateType: chosenLicensePlateType,
            licensePlate: chosenLicensePlateText
        },
        resolver: yupResolver(LicensePlateValidationSchema)
    });

    const { subscribe, setValue, getValues } = methods;

    useEffect(() => {
        if (!chosenLicensePlateText) {
            return;
        }

        const currentFormValue = getValues("licensePlate");

        if (currentFormValue !== chosenLicensePlateText) {
            setValue("licensePlate", chosenLicensePlateText);
        }
    }, [chosenLicensePlateText, setValue, getValues]);

    useEffect(() => {
        const currentFormValue = getValues("licensePlateType");

        if (currentFormValue !== chosenLicensePlateType) {
            setValue("licensePlateType", chosenLicensePlateType);
            setValue(
                "isPersonalized",
                chosenLicensePlateType ===
                    LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED
            );
        }
    }, [chosenLicensePlateType, setValue, getValues]);

    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values, name }) => {
                if (name === "isPersonalized") {
                    const newType = values.isPersonalized
                        ? LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED
                        : LicensePlateTypes.LICENSE_PLATE_CAR_STANDARD;
                    if (newType !== values.licensePlateType) {
                        setValue("licensePlate", "");
                        setValue("licensePlateType", newType);
                        onConfigurationChange({
                            chosenLicensePlateType: newType
                        });
                        setConfigurationValid(false);
                    }
                }

                if (name === "licensePlate") {
                    const value = values.licensePlate || "";
                    if (
                        values.licensePlateType ===
                            LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED &&
                        value.length
                    ) {
                        onConfigurationChange({
                            chosenLicensePlateText: value
                        });
                        return setConfigurationValid(true);
                    }

                    const valid = /[0-9A-Z]{1}-[A-Z]{3}-[0-9]{3}/.test(value);

                    if (valid) {
                        onConfigurationChange({
                            chosenLicensePlateText: value
                        });
                    }

                    setConfigurationValid(valid);
                }
            }
        });

        return () => callback();
    }, [subscribe, onConfigurationChange, setValue, setConfigurationValid]);

    return (
        <div className={cn("tw:mt-4", className)}>
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form id={FORM_ID}>
                            <HiddenField<LicensePlateFormValues> name="licensePlateType" />
                            <LicensePlateInput />
                            <CheckBoxField<LicensePlateFormValues>
                                name="isPersonalized"
                                required={false}
                                optionalIndication={false}
                                label={t("ecommerce.licensePlate.personalized")}
                            />
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
            <p
                dangerouslySetInnerHTML={{
                    __html: t("ecommerce.licensePlate.personalizedOption")
                }}
            />
        </div>
    );
};

export default LicensePlate;
